import { Outlet, useLocation, useNavigate } from "@tanstack/react-router"
import { useEffect } from "react"
import { JugglingScreen } from "@motley/design"
import { useGetSettingsQuery, useUpdateSettingsMutation } from "./api"
import { getTenantSlug, useRedirectAfterLogin } from "@/auth"
import { useSelector } from "@/base"

type Props = {
  tenantSlug: string | undefined
}

export default function TenantRoute({ tenantSlug }: Props) {
  const location = useLocation()
  const { setRedirectUrl } = useRedirectAfterLogin()
  const { data: settings, isLoading: isSettingsLoading } = useGetSettingsQuery()
  const [updateSettings] = useUpdateSettingsMutation()
  const navigate = useNavigate()
  // Not settings: they can still be refetching the workspace left behind
  const scopedSlug = useSelector(getTenantSlug)

  const currentTenantSlug = settings?.currentTenantSlug

  // BC: Redirect to prefixed path
  useEffect(() => {
    if (tenantSlug) {
      return
    }

    if (!scopedSlug) {
      setRedirectUrl()
      void navigate({ to: "/onboarding" })

      return
    }

    if (location.pathname.startsWith("/t/")) {
      return
    }

    void navigate({
      to: `/t/${scopedSlug}${location.pathname}`,
      search: location.search,
      replace: true,
    })
  }, [
    scopedSlug,
    setRedirectUrl,
    location.pathname,
    location.search,
    navigate,
    tenantSlug,
  ])

  // Store as current when switching
  useEffect(() => {
    if (!tenantSlug || isSettingsLoading) {
      return
    }
    if (currentTenantSlug === tenantSlug) {
      return
    }

    void updateSettings({ currentTenantSlug: tenantSlug })
  }, [currentTenantSlug, isSettingsLoading, tenantSlug, updateSettings])

  // A slug-less path only redirects
  if (!tenantSlug) {
    return <JugglingScreen />
  }

  return <Outlet />
}
